import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { api, globalErrorHandler } from '../../api/create-api';
import { Row, Col, Button, ButtonGroup, UncontrolledButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem, Alert, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Prompt } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPlus, faEdit, faList, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Field } from '@galop/component-library';
import { CancelButton, SelectCollection, SearchableSelect } from '@galop/component-library';

import { FuzzyDate, GetOptionText, groupBy, NumericizeObjectProperties, userRoles } from '../../Helpers/Helper';
import { makeCall } from '../../auth-utils';
import Can from '../Shared/Can';
import { DeleteButton } from '@galop/component-library';
import { useFormFields } from '../Shared/FormFields';
import { InviteUser } from './InviteUser';
import { AppName } from '../Admin/Admin';

export const ViewUser = (props) => {
    const ls = require('local-storage');
    const { config, IsDirty, resource, userId, Close, Save, NewResource, ReloadResources } = props;
    const { addToast, removeToast } = useToasts();

    /* Generic Variables */
    const [resourceRecord, updateResource, setResourceRecord] = useFormFields(resource || {}, () => setResourceIsDirty(true));
    const [editResource, setEditResource] = useState(NewResource);
    const [resourceIsDirty, setResourceIsDirty] = useState(false);

    /* Resource Specific Variables */
    const [redirect, setRedirect] = useState('');
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        IsDirty(resourceIsDirty || editing);
    }, [resourceIsDirty, editing]);

    useEffect(() => {
        if (resource) {
            setResourceRecord(resource);
            if (('loaded' in resource) && !resource.loaded) {
                addToast(`Loading ${resource.name} data...`, { appearance: 'info', id: 'loading-data' });
            }
            else {
                removeToast('loading-data');
            }
        }
    }, [resource]);

    const getResource = () => {
        makeCall(async (authHeader) => {
            const result = await api(`api/${config.controllerName}/${resource.id}`, {
                headers: authHeader
            })
                .catch((error) => globalErrorHandler(error, addToast));

            if (result && result.data) {
                setResourceRecord(result.data);
            }
        });
    };

    const deleteResource = async () => {
        makeCall(async (authHeader) => {
            addToast(`Deleting ${config.capitalResourceName}`, { appearance: 'info', id: 'deleting-resource' });
            await api.delete(`api/${config.controllerName}/${resourceRecord.id}`, {
                headers: authHeader
            }).catch((error) => globalErrorHandler(error, addToast));
            removeToast('deleting-resource', () => {
                setResourceIsDirty(false);
                if (Save) {
                    Save({ ...resourceRecord, deleted: !resource.deleted });
                }

                if (ReloadResources) {
                    ReloadResources();
                }

                Close();
            });
        });
    };

    const goToPage = (resource, to) => {
        if (resource) {
            ls(config.localStorage, resource);
        }
        setRedirect(to);
    };

    const resourceIsValid = () => {
        let isValid = true;
        isValid = isValid && (resourceRecord.name); /* Check for Email */

        return isValid;
    };

    const saveResource = (email) => {
        makeCall(async (authHeader) => {
            if (resourceRecord.userName || email) {
                if (resourceRecord.role || email) {
                    addToast(`Saving ${resourceRecord.firstName} ${resourceRecord.lastName}`, { appearance: 'info', id: 'saving-resource' });
                    let userToUpdate = resourceRecord;
                    let method = 'put';
                    let url = `api/users`;
                    if (email) {
                        method = 'post';
                        url = `api/users`;
                        userToUpdate.email = email;
                        userToUpdate.userName = email;
                    }
                    const result = await api(
                        {
                            method,
                            url,
                            data: userToUpdate,
                            headers: authHeader
                        })
                        .catch((error) => globalErrorHandler(error, addToast));

                    if (result) {
                        if (email) {
                            //ReloadResources();
                            Save(result.data);
                        }
                        else {
                            Save(userToUpdate);
                            setResourceIsDirty(false);
                            setEditResource(false);
                        }
                    }

                    removeToast('saving-resource');
                }
                else {
                    addToast('Please select a Role', { appearance: 'error', autoDismiss: true });
                }
            }
            else {
                addToast('Username is not valid!', { appearance: 'error', autoDismiss: true });
            }
        });
    };

    const access = (
        <Row>
            <Col sm="8" lg="6" xl="4">
                <Field type="select" field="role" label="Role" value={resourceRecord.role} onChange={updateResource} EnableInput={{ disabled: !editResource }}>
                    <option>Select a Role</option>
                    {userRoles.map(role => <option key={role.value} value={role.value}>{role.text}</option>)}
                </Field>
            </Col>
        </Row>
    );

    return (
        !NewResource ?
            <div className="mb-2 d-flex flex-column h-100">
                <Prompt when={resourceIsDirty} message='You have unsaved changes, are you sure you want to leave?' />
                <div className="mt-md-0 mt-2 mb-1">
                    <div>
                        <Row>
                            <Col>
                                <Button color="link" className="d-inline-block d-md-none py-0" onClick={Close}><FontAwesomeIcon icon={faChevronLeft} /></Button>
                                <span className="h3 d-inline-block align-middle text-dark pb-0 mb-0">{resourceRecord.firstName || resourceRecord.lastName ? `${resourceRecord.firstName || ''} ${resourceRecord.lastName || ''}` : ""}</span>
                                {!editResource && <span> <a href={`mailto:${resourceRecord.email}`} className="text-secondary"><FontAwesomeIcon icon={faEnvelope} /></a></span>}
                            </Col>
                        </Row>
                    </div>
                </div>
                <div className="flex-fill scrollable-list border-top border-bottom" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                    <div className="ps-1 pr-3">
                        <Row className="text-muted">
                            <Col>
                                <small><em>{resourceRecord.lastLoggedIn ? FuzzyDate(resourceRecord.lastLoggedIn) : null}</em></small>
                            </Col>
                            <Col className="text-end">
                                <em>{(resourceRecord.userName || '').replace('@greystar.com', '')}</em>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <Field field="firstName" label="First Name" value={resourceRecord.firstName} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                            </Col>
                            <Col>
                                <Field field="lastName" label="Last Name" value={resourceRecord.lastName} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                            </Col>
                            <Col sm="4">
                                <Field field="email" label="Email" value={resourceRecord.email} onChange={updateResource} EnableInput={{ disabled: !editResource }} />
                            </Col>
                        </Row>
                        {access}
                    </div>
                </div>
                <Row className="pt-1">
                    {!NewResource && (resourceRecord.deleted ?
                        <Can role={config.writeRole}
                            yes={() => <Col>
                                <DeleteButton color="danger" Close={deleteResource} ShowDropdown={true}>Delete</DeleteButton>
                            </Col>}
                        />
                        : <Can role={config.writeRole}
                            yes={() => <Col>
                                <DeleteButton color="danger" outline Close={deleteResource} ShowDropdown={true}>Delete</DeleteButton>
                            </Col>}
                        />)}
                    {!editResource ?
                        <Col className="text-end">
                            <Button color="light" className="border me-1" onClick={Close}>Close</Button>
                            <Can role={config.writeRole}
                                yes={() => <Button color="secondary" onClick={() => setEditResource(true)}>Edit</Button>}
                            />
                        </Col>
                        :
                        <Col className="text-end">
                            <CancelButton color="light" className="border me-1" Close={resource && !resourceIsDirty && !resource.new ? () => {
                                setResourceRecord(resource);
                                setResourceIsDirty(false);
                                setEditResource(false);
                            } : Close} Reload={() => {
                                setResourceRecord(resource);
                                setResourceIsDirty(false);
                            }} ShowDropdown={resourceIsDirty}>Cancel</CancelButton>
                            <Button color="success" disabled={!resourceIsDirty} onClick={() => saveResource()}>Save</Button>
                        </Col>

                    }
                </Row>
            </div>
            :
            <InviteUser className="mb-2 d-flex flex-column h-100" AppName={AppName} CurrentUserName={props.CurrentUser.name} InvitedUser={(email) => {
                saveResource(email);
            }} Close={Close}>
                <div className="flex-fill scrollable-list border-top border-bottom p-1" style={{ overflow: 'scroll' }}>
                    {access}
                </div>
            </InviteUser>
    );
};


// WEBPACK FOOTER //
// ./src/components/Users/ViewUser.js